import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../api";
import { useTranslation } from "../../../hooks/useTranslation";
import { useLanguage } from "../../../Translations/LanguageContext";
import { useInView } from "../../../hooks/useInView";
import NewsletterSignup from "../../NewsletterSignup/NewsletterSignup";

// The site-wide footer — every page renders this one (see AppContent.js),
// not just the landing page. `cities` and `onGoBusiness` are optional: the
// landing page passes its own (already-fetched cities, and a local
// view-switcher instead of a real navigation, since its business portal is
// a client-side sub-view, not a route). Everywhere else, this fetches its
// own city list and "For Business" just navigates home into that view.
const LandingFooter = ({ cities: citiesProp, onGoBusiness }) => {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const [ref, isInView] = useInView(0.15);
  const navigate = useNavigate();
  const [fetchedCities, setFetchedCities] = useState([]);

  useEffect(() => {
    if (citiesProp !== undefined) return;
    api
      .getAllCities()
      .then((res) => setFetchedCities(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Error fetching cities for footer:", err));
  }, [citiesProp]);

  const cities = citiesProp !== undefined ? citiesProp : fetchedCities;
  const goBusiness = onGoBusiness || (() => navigate("/", { state: { view: "business" } }));

  return (
    <footer className="sp-site-footer" style={{ background: "var(--color-neutral-900)", color: "var(--color-neutral-100)", padding: "48px 32px 24px" }}>
      <div style={{ maxWidth: 440, margin: "0 auto 40px", textAlign: "center" }}>
        <div style={{ fontFamily: "var(--font-heading)", fontSize: 21, marginBottom: 8 }}>
          {t.landingNewsletterHeading}
        </div>
        <p style={{ opacity: 0.65, fontSize: 14, marginBottom: 20 }}>{t.landingNewsletterBlurb}</p>
        <div style={{ textAlign: "left" }}>
          <NewsletterSignup cities={cities} source="website_footer" />
        </div>
      </div>
      <div className="hr" style={{ background: "rgba(255,255,255,0.12)", margin: "0 auto 40px", maxWidth: 1240 }} />

      <div
        ref={ref}
        className={`sp-footer-grid sp-reveal sp-reveal-up sp-reveal-group${isInView ? " is-visible" : ""}`}
        style={{ maxWidth: 1240, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 32 }}
      >
        <div>
          <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.55, marginBottom: 12 }}>
            {t.landingFooterAbout}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
            <Link to="/about" style={{ color: "inherit", opacity: 0.8, textDecoration: "none" }}>{t.landingFooterWhatIsSp}</Link>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.55, marginBottom: 12 }}>
            {t.landingFooterCities}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
            {cities.map((c) => (
              <span key={c._id || c.name} style={{ opacity: 0.8 }}>{c.name}</span>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.55, marginBottom: 12 }}>
            {t.landingFooterCompany}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
            <a
              href="#business"
              style={{ color: "inherit", opacity: 0.8, textDecoration: "none" }}
              onClick={(e) => {
                e.preventDefault();
                goBusiness();
              }}
            >
              {t.landingNavBusiness}
            </a>
            <a href="#about" style={{ color: "inherit", opacity: 0.8, textDecoration: "none" }}>{t.landingNavAbout}</a>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.55, marginBottom: 12 }}>
            {t.landingFooterConnect}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <a
              href="https://wa.me/447732171486"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "inherit",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.18)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
            >
              <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.336-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
              </svg>
            </a>
            <a
              href="https://instagram.com/smallpotatoes_uk"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "inherit",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.18)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.55, marginBottom: 12 }}>
            {t.landingFooterLanguage}
          </div>
          <button
            className="btn btn-secondary"
            style={{ background: "transparent", borderColor: "rgba(255,255,255,0.3)", color: "var(--color-neutral-100)" }}
            onClick={() => setLanguage(language === "zh" ? "en" : "zh")}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18M12 3c2.5 2.6 4 5.9 4 9s-1.5 6.4-4 9c-2.5-2.6-4-5.9-4-9s1.5-6.4 4-9Z" />
            </svg>
            {t.landingFooterLangLabel}
          </button>
        </div>
      </div>
      <div className="hr" style={{ background: "rgba(255,255,255,0.12)", margin: "28px auto 16px", maxWidth: 1240 }} />
      <div
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          fontSize: 12,
          opacity: 0.5,
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <span>© {new Date().getFullYear()} small potatoes. {t.landingFooterCopyright}.</span>
        <Link to="/privacy-policy" style={{ color: "inherit" }}>{t.landingFooterPrivacy}</Link>
      </div>
    </footer>
  );
};

export default LandingFooter;
